import { reactive } from 'vue';
import axios from 'axios';

// The school's identity (name, logo, colour…) comes from the backend's
// School Settings so one codebase serves any school. These defaults are only
// used if the API can't be reached, so the app still renders.
const API = process.env.VUE_APP_SERVER;

export const branding = reactive({
  loaded: false,
  name: 'Class Management System',
  shortName: 'CMS',
  tagline: '',
  depedSchoolId: '',
  address: '',
  district: '',
  division: '',
  region: '',
  brandColor: '#E35E93',
  emailSenderName: '',
  logoUrl: '/img/southernlogo.png',
  // Sign-in page brand panel (School Settings › Sign-in page)
  loginHeadline: '',
  loginOverlay: 60,
  loginShowFeatures: true,
  // School contacts, printed in the Form 29 footer.
  contactPhone: '',
  contactEmail: '',
  // Form 29 signatories (DepEd personnel ids), see School Settings.
  form29CheckedByJhsID: null,
  form29CheckedByShsID: null,
  form29RecommendingID: null,
  form29ApprovedByID: null,
  // Or typed in by hand; a typed name prints instead of the pick.
  form29PreparedByJhsName: '',
  form29PreparedByJhsPosition: '',
  form29PreparedByShsName: '',
  form29PreparedByShsPosition: '',
  form29CheckedByJhsName: '',
  form29CheckedByJhsPosition: '',
  form29CheckedByShsName: '',
  form29CheckedByShsPosition: '',
  form29ReviewedBy1Name: '',
  form29ReviewedBy1Position: '',
  form29ReviewedBy2Name: '',
  form29ReviewedBy2Position: '',
  form29RecommendingName: '',
  form29RecommendingPosition: '',
  form29ApprovedByName: '',
  form29ApprovedByPosition: '',
  assets: {},
});

let vuetifyInstance = null;

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  return m ? m.slice(1).map((h) => parseInt(h, 16)) : [227, 94, 147];
}

function applyToPage() {
  const root = document.documentElement;
  const [r, g, b] = hexToRgb(branding.brandColor);
  root.style.setProperty('--brand', branding.brandColor);
  root.style.setProperty('--brand-rgb', `${r}, ${g}, ${b}`);
  root.style.setProperty('--brand-soft', `rgba(${r}, ${g}, ${b}, 0.1)`);
  // Readable text on the brand colour; same rule as the School Settings
  // preview (BrandPreview.vue), so a light school colour gets dark text.
  const light = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
  root.style.setProperty('--brand-foreground', light ? '#0a0a0a' : '#ffffff');

  if (vuetifyInstance) {
    for (const name of ['light', 'dark']) {
      const theme = vuetifyInstance.theme.themes.value[name];
      if (theme) {
        // The school's colour drives primary actions as well as accents.
        theme.colors.primary = branding.brandColor;
        theme.colors.brand = branding.brandColor;
      }
    }
  }

  document.title = branding.shortName
    ? `${branding.shortName} · Class Management`
    : 'Class Management';

  let icon = document.querySelector("link[rel~='icon']");
  if (!icon) {
    icon = document.createElement('link');
    icon.rel = 'icon';
    document.head.appendChild(icon);
  }
  icon.href = branding.logoUrl;
}

export function applyBranding(data) {
  if (!data) return;
  Object.assign(branding, {
    name: data.name ?? branding.name,
    shortName: data.shortName ?? branding.shortName,
    tagline: data.tagline ?? '',
    depedSchoolId: data.depedSchoolId ?? '',
    address: data.address ?? '',
    district: data.district ?? '',
    division: data.division ?? '',
    region: data.region ?? '',
    brandColor: data.brandColor ?? branding.brandColor,
    emailSenderName: data.emailSenderName ?? '',
    loginHeadline: data.loginHeadline ?? '',
    loginOverlay: data.loginOverlay ?? 60,
    loginShowFeatures: data.loginShowFeatures ?? true,
    contactPhone: data.contactPhone ?? '',
    contactEmail: data.contactEmail ?? '',
    form29CheckedByJhsID: data.form29CheckedByJhsID ?? null,
    form29CheckedByShsID: data.form29CheckedByShsID ?? null,
    form29RecommendingID: data.form29RecommendingID ?? null,
    form29ApprovedByID: data.form29ApprovedByID ?? null,
    form29PreparedByJhsName: data.form29PreparedByJhsName ?? '',
    form29PreparedByJhsPosition: data.form29PreparedByJhsPosition ?? '',
    form29PreparedByShsName: data.form29PreparedByShsName ?? '',
    form29PreparedByShsPosition: data.form29PreparedByShsPosition ?? '',
    form29CheckedByJhsName: data.form29CheckedByJhsName ?? '',
    form29CheckedByJhsPosition: data.form29CheckedByJhsPosition ?? '',
    form29CheckedByShsName: data.form29CheckedByShsName ?? '',
    form29CheckedByShsPosition: data.form29CheckedByShsPosition ?? '',
    form29ReviewedBy1Name: data.form29ReviewedBy1Name ?? '',
    form29ReviewedBy1Position: data.form29ReviewedBy1Position ?? '',
    form29ReviewedBy2Name: data.form29ReviewedBy2Name ?? '',
    form29ReviewedBy2Position: data.form29ReviewedBy2Position ?? '',
    form29RecommendingName: data.form29RecommendingName ?? '',
    form29RecommendingPosition: data.form29RecommendingPosition ?? '',
    form29ApprovedByName: data.form29ApprovedByName ?? '',
    form29ApprovedByPosition: data.form29ApprovedByPosition ?? '',
    assets: Object.fromEntries(
      Object.entries(data.assets ?? {}).map(([kind, a]) => [
        kind,
        { ...a, url: API + a.url },
      ]),
    ),
  });
  if (branding.assets.logo) branding.logoUrl = branding.assets.logo.url;
  branding.loaded = true;
  applyToPage();
}

// Called once before the app mounts. Never throws: a slow or failed request
// falls back to the defaults after a short wait.
export async function loadBranding(vuetify) {
  vuetifyInstance = vuetify;
  try {
    const res = await axios.get(`${API}/school-settings`, { timeout: 4000 });
    applyBranding(res.data);
  } catch (e) {
    applyToPage();
  }
}

export function installBranding(app) {
  app.config.globalProperties.$branding = branding;
}

export function useBranding() {
  return branding;
}
